import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useCatalogDialog } from "@/hooks/use-catalog-dialog";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useTranslation } from "react-i18next";
import { DirtyIndicator } from "@/components/ui/dirty-indicator";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { useSearch } from "@tanstack/react-router";
import { getCurrentUser } from "@/lib/api/auth";

import { formatAdminDate } from "@/components/admin/formatting";
import { DeletedIndicatorCell } from "@/components/admin/catalogs/deleted-indicator-cell";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CustomSelect } from "@/components/ui/custom-select";
import { ErrorState, Text } from "@/components/ui/text";
import { Fab } from "@/components/ui/fab";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { FormLabel } from "@/components/ui/form-label";
import { Icon } from "@/components/ui/icons";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { ModalBody, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { PageTitle } from "@/contexts/page-title-context";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { ResizeHandle } from "@/components/ui/resize-handle";
import {
  ResizableTableShell,
  type ColumnDef,
} from "@/components/ui/resizable-table-shell";
import { HStack } from "@/components/ui/h-stack";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { TableViewport } from "@/components/ui/table-viewport";
import { VStack } from "@/components/ui/v-stack";
import { useSizeClass } from "@/hooks/use-size-class";
import { TableSkeleton } from "@/components/admin/table-skeleton";
import type { Ref } from "@/lib/api/types";
import type { TruckModel } from "@/lib/api/trucks";
import { getTruckManufacturers, getTruckModels } from "@/lib/api/trucks";
import { createTruckModel, updateTruckModel } from "@/lib/api/catalogs";
import { ConfirmActionButton } from "@/components/admin/catalogs/confirm-action-button";
import { DeletedWarningBanner } from "@/components/admin/catalogs/deleted-warning-banner";
import { useDeleteRestore } from "@/hooks/useDeleteRestore";
import { useForm } from "@/hooks/useForm";
import { z } from "zod";
import { useGuardedClose } from "@/hooks/useGuardedClose";

type TruckModelRow = TruckModel & { created_at?: string; is_deleted?: boolean };

const ALL_MANUFACTURERS_VALUE = "All";

function AdminTruckModelsCatalogPage() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tCatalogTruckModels } = useTranslation(
    "admin.catalogs.truckModels",
  );
  const { t: tCommon } = useTranslation("common");
  const searchParams = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const initialManufacturerId =
    searchParams.manufacturer_id ?? ALL_MANUFACTURERS_VALUE;

  const queryClient = useQueryClient();
  const { offset } = usePageOffset();
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<string>(
    initialManufacturerId,
  );
  const { data: manufacturersData } = useQuery({
    queryKey: ["admin", "catalogs", "truck-makes", "all"],
    queryFn: () => getTruckManufacturers({ limit: 200 }),
    staleTime: 60_000,
  });
  const manufacturers = (manufacturersData?.data ?? []) as Ref[];
  const {
    data: modelsData,
    isLoading: loading,
    isError: loadError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.admin.truckModels(selectedManufacturerId, {
      limit: DEFAULT_PAGE_SIZE,
      offset,
    }),
    queryFn: () =>
      getTruckModels(
        selectedManufacturerId === ALL_MANUFACTURERS_VALUE
          ? { limit: DEFAULT_PAGE_SIZE, offset }
          : {
              limit: DEFAULT_PAGE_SIZE,
              offset,
              truck_manufacturer_ids: [selectedManufacturerId],
            },
      ),
    placeholderData: keepPreviousData,
  });
  const models = useMemo(
    () => (modelsData?.data ?? []) as unknown as TruckModelRow[],
    [modelsData?.data],
  );
  const total = modelsData?.total ?? 0;
  const { page, totalPages, setPage } = usePagination({ total });
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const { filters: searchFilters, setFilter: setSearchFilter } =
    useFilterSearchParams({ keys: ["search"] });
  const query = searchFilters.search;
  const setQuery = (v: string) => setSearchFilter("search", v);

  const {
    isOpen: modalOpen,
    selectedItem: editing,
    openCreate: catalogOpenCreate,
    openEdit: catalogOpenEdit,
    close: catalogClose,
  } = useCatalogDialog<TruckModelRow>();
  const form = useForm({
    schema: z.object({
      modalManufacturerId: z.string(),
      name: z.string().min(1),
    }),
    defaults: { modalManufacturerId: "", name: "" },
    onSubmit: async () => {},
  });
  const formData = form.fields;
  const isDirty = form.isDirty;
  const setFormData = (updater: (prev: typeof formData) => typeof formData) => {
    form.setFields(updater(form.fields));
  };
  const { handleClose, confirmDialog: closeConfirmDialog } = useGuardedClose(
    isDirty,
    catalogClose,
  );

  const { loading: deleteLoading, toggle: toggleDelete } =
    useDeleteRestore<TruckModelRow>({
      endpoint: "/truck-models",
      onSuccess: (_isDeleted, data) => {
        catalogOpenEdit(data as TruckModelRow);
        void queryClient.invalidateQueries({
          queryKey: [
            "admin",
            "catalogs",
            "truck-makes",
            selectedManufacturerId,
            "models",
          ],
        });
      },
    });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return models;
    return models.filter((m) => {
      return (
        m.name.toLowerCase().includes(q) ||
        (m.manufacturer?.name ?? "").toLowerCase().includes(q)
      );
    });
  }, [models, query]);

  function openCreate() {
    catalogOpenCreate();
    form.reset({
      name: "",
      modalManufacturerId:
        selectedManufacturerId === ALL_MANUFACTURERS_VALUE
          ? ""
          : selectedManufacturerId,
    });
  }

  function openEdit(item: TruckModelRow) {
    catalogOpenEdit(item);
    form.reset({
      name: item.name,
      modalManufacturerId: item.manufacturer?.id ?? "",
    });
  }

  const isValid = useMemo(
    () =>
      Boolean(formData.name.trim()) &&
      (Boolean(editing) || Boolean(formData.modalManufacturerId)),
    [editing, formData],
  );

  async function handleSave() {
    try {
      setSubmitting(true);
      setSaveError(false);

      if (editing) {
        await updateTruckModel(editing.id, { name: formData.name });
      } else {
        await createTruckModel({
          name: formData.name,
          manufacturer_id: formData.modalManufacturerId,
        });
      }

      void queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "catalogs",
          "truck-makes",
          selectedManufacturerId,
          "models",
        ],
      });
      form.reset();
      catalogClose();
    } catch (error) {
      console.error("Failed to save truck model", error);
      setSaveError(true);
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Resizable columns ──────────────────────────────────────────────
  const userId = getCurrentUser()?.id ?? null;
  const columnDefs: ColumnDef[] = [
    { key: "deleted", defaultWidth: 40, minWidth: 40 },
    { key: "name", defaultWidth: 200, minWidth: 100 },
    { key: "manufacturer", defaultWidth: 180, minWidth: 100 },
    { key: "created", defaultWidth: 140, minWidth: 80 },
    { key: "actions", defaultWidth: 120, minWidth: 80 },
  ];

  return (
    <VStack gap="lg" data-testid="admin-catalog-truck-models-page">
      <VStack gap="none">
        <PageTitle>{tCatalogTruckModels("title")}</PageTitle>
      </VStack>
      <StickyHeaderSlot>
        <Card variant="bordered" padding="xs">
          <FilterToolbar
            searchPlaceholder={tCatalogTruckModels("searchPlaceholder")}
            searchValue={query}
            onSearchChange={setQuery}
            createAction={{
              label: tCatalogTruckModels("add"),
              onClick: openCreate,
            }}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
            advancedFilters={[
              <CustomSelect
                key="manufacturer-filter"
                value={selectedManufacturerId}
                onChange={(value) => setSelectedManufacturerId(value)}
                options={[
                  {
                    value: ALL_MANUFACTURERS_VALUE,
                    label: tCatalogTruckModels("manufacturerFilterAll"),
                  },
                  ...manufacturers.map((m) => ({
                    value: m.id,
                    label: m.name,
                  })),
                ]}
                label={tCatalogTruckModels("fields.manufacturer")}
              />,
            ]}
            activeFilterCount={
              selectedManufacturerId !== ALL_MANUFACTURERS_VALUE ? 1 : 0
            }
            onClearFilters={() =>
              setSelectedManufacturerId(ALL_MANUFACTURERS_VALUE)
            }
            data-testid="catalog-truck-models-toolbar"
          />
        </Card>
      </StickyHeaderSlot>

      <Card variant="bordered" padding="none">
        <TableViewport position="relative">
          {loadError || saveError ? (
            <ErrorState
              title={
                loadError
                  ? tCatalogTruckModels("failedToLoad")
                  : tCatalogTruckModels("failedToSave")
              }
              action={
                loadError ? (
                  <Button
                    color="neutral"
                    variant="outline"
                    onClick={() => void refetch()}
                  >
                    {tCommon("retry")}
                  </Button>
                ) : undefined
              }
              className="py-10"
            />
          ) : null}

          {loading && models.length === 0 ? (
            <TableSkeleton
              headers={[
                "",
                tCatalogTruckModels("columns.name"),
                tCatalogTruckModels("columns.manufacturer"),
                tCatalogTruckModels("columns.created"),
                tCatalogTruckModels("columns.actions"),
              ]}
            />
          ) : filtered.length === 0 ? (
            <Text color="muted">{tCatalogTruckModels("noItems")}</Text>
          ) : (
            <ResizableTableShell
              tableId="catalog-truck-models"
              userId={userId}
              columns={columnDefs}
            >
              {({ onResizeStart }) => (
                <>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell className="w-10" />
                      <TableHeaderCell className="relative">
                        {tCatalogTruckModels("columns.name")}
                        <ResizeHandle
                          columnKey="name"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="relative">
                        {tCatalogTruckModels("columns.manufacturer")}
                        <ResizeHandle
                          columnKey="manufacturer"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="relative">
                        {tCatalogTruckModels("columns.created")}
                        <ResizeHandle
                          columnKey="created"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="relative text-right" sticky>
                        {tCatalogTruckModels("columns.actions")}
                        <ResizeHandle
                          columnKey="actions"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="w-auto" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((m) => {
                      const isDeleted = Boolean(m.is_deleted);
                      const mutedClass = isDeleted
                        ? "text-[color:var(--on-surface-variant)] opacity-70"
                        : "";
                      return (
                        <TableRow key={m.id}>
                          <TableCell className="w-10">
                            <DeletedIndicatorCell isDeleted={isDeleted} />
                          </TableCell>
                          <TableCell className={`font-medium ${mutedClass}`}>
                            {m.name}
                          </TableCell>
                          <TableCell className={mutedClass}>
                            {m.manufacturer?.name ?? "—"}
                          </TableCell>
                          <TableCell className={mutedClass}>
                            {formatAdminDate(m.created_at, language)}
                          </TableCell>
                          <TableCell className="text-right" sticky>
                            <Button
                              type="button"
                              color="neutral"
                              variant="outline"
                              size="sm"
                              onClick={() => openEdit(m)}
                            >
                              {tCatalogTruckModels("edit")}
                            </Button>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </>
              )}
            </ResizableTableShell>
          )}
          {loading && models.length > 0 && (
            <PageLoadingOverlay
              page={page}
              label={tCommon("pagination.loadingPage")}
            />
          )}
        </TableViewport>
      </Card>

      {isCompact && (
        <Fab
          onClick={openCreate}
          aria-label={tCatalogTruckModels("add")}
          data-testid="catalog-truck-models-fab"
        />
      )}

      <AdaptiveSheet
        open={modalOpen}
        onDismiss={handleClose}
        onSubmit={() => void handleSave()}
        size="md"
        data-testid="admin-truck-model-modal"
      >
        <ModalHeader>
          <Text weight="medium">
            {editing
              ? tCatalogTruckModels("modalEditTitle")
              : tCatalogTruckModels("modalAddTitle")}
            {isDirty ? <DirtyIndicator /> : null}
          </Text>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={submitting}
          >
            <Icon.X size="sm" />
          </Button>
        </ModalHeader>
        <ModalBody className="space-y-4">
          {editing && editing.is_deleted ? <DeletedWarningBanner /> : null}
          <CustomSelect
            value={formData.modalManufacturerId}
            disabled={Boolean(editing) || submitting}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                modalManufacturerId: value,
              }))
            }
            fullWidth
            label={tCatalogTruckModels("fields.manufacturer")}
            labelPosition="above"
            options={[
              {
                value: "",
                label: tCatalogTruckModels("manufacturerSelect"),
              },
              ...manufacturers.map((m) => ({
                value: m.id,
                label: m.name,
              })),
            ]}
          />
          <VStack gap="xs">
            <FormLabel>{tCatalogTruckModels("fields.name")}</FormLabel>
            <InputWrapper
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              disabled={submitting}
              data-testid="admin-truck-model-name"
            />
          </VStack>
          {editing ? (
            <Text size="xs" color="muted">
              {tCatalogTruckModels("manufacturerLockedNote")}
            </Text>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <HStack align="stretch" gap="lg">
            {editing ? (
              <ConfirmActionButton
                isRestore={Boolean(editing.is_deleted)}
                loading={deleteLoading}
                disabled={submitting}
                onConfirm={() =>
                  void toggleDelete(editing.id, Boolean(editing.is_deleted))
                }
                className="mr-3"
              />
            ) : null}
            <Button
              type="button"
              color="neutral"
              variant="outline"
              onClick={handleClose}
              disabled={submitting || deleteLoading}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              type="button"
              loading={submitting}
              disabled={
                submitting ||
                deleteLoading ||
                !isValid ||
                (Boolean(editing) && !isDirty)
              }
              onClick={() => void handleSave()}
            >
              {tCommon("save")}
            </Button>
          </HStack>
        </ModalFooter>
      </AdaptiveSheet>
      {closeConfirmDialog}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/catalogs/truck-models")({
  component: AdminTruckModelsCatalogPage,
});
